import { logException } from '../log/logException';
import { getTimeZoneName } from './getTimeZoneName';
import { getTimeZoneOffset } from './getTimeZoneOffset';
import { GMT_TRANSLATIONS } from './timeZones';

export interface TimeZoneFormatOptions {
    /** optional date that will be used for retrieving the timezones */
    date?: Date;
    /** language that the time zone will be displayed */
    language: string;
    /** if true, the formatted time zone includes the time zone name */
    withTimeZoneName?: boolean;
    /** whether the offset (GMT+/-x) should be displayed or not */
    withTimeZoneOffset?: boolean;
    /** format for the time zone, `long` = `Central European Time`, `short` = `Europe/Paris` */
    timeZoneNameFormat?: 'long' | 'short';
    /** if true, the formatted time zone will include the time zone's location, usually a city name. */
    withLocation?: boolean;
    /** whether the text should be manipulated depending on the language. */
    withLanguageFormatting?: boolean;
}

export interface GetFormattedTimeZoneOptions extends TimeZoneFormatOptions {
    /** time zone to format */
    timeZone: string;
}

/**
 * Converts a time zone in the format `Europe/Paris` to multiple alternatives
 * including the difference with GMT, time zone long name (like Central European Time).
 *
 * If nothing is passed in, we just display GMT+02:00, which is the offset with GMT
 * @param GetFormattedTimeZoneOptions
 * @returns Formatted TimeZone
 */
export const getFormattedTimezone = ({
    date,
    language,
    withTimeZoneName = false,
    withLocation = false,
    withLanguageFormatting = true,
    withTimeZoneOffset = true,
    timeZoneNameFormat = 'long',
    timeZone,
}: GetFormattedTimeZoneOptions) => {
    let formattedTimeZone = '';

    try {
        /**
         * By default, we just display the offset with GMT
         */
        const timeZoneOffset = getTimeZoneOffset({ date, timeZone, language });
        if (withTimeZoneOffset) {
            formattedTimeZone = `${timeZoneOffset}`;
        }

        /**
         * If we need to add a time zone name, we create it depending on the format demanded, and we add it at the end
         * of the timezone name.
         */
        if (withTimeZoneName) {
            const timeZoneName =
                timeZoneNameFormat === 'long'
                    ? getTimeZoneName({ date, timeZone, language, withLanguageFormatting })
                    : timeZone;
            formattedTimeZone = formattedTimeZone ? `${formattedTimeZone} - ${timeZoneName}` : timeZoneName;
        }

        /**
         * If we want to show the location (for example the city), we retrieve the location name using the
         * `shortGeneric` format.
         */
        if (withLocation) {
            const locationName = getTimeZoneName({
                date,
                timeZone,
                language,
                format: 'shortGeneric',
                withLanguageFormatting,
            });

            /**
             * In order to avoid duplicates, we validate that the text that we are displaying for the location
             * is not already present in the formatted time zone. This avoid cases like:
             * - GMT+00:00 - Azores Time - Azores Time => with this `if` it prints GMT+00:00 - Azores Time
             * - GMT+00:00 - Greenwhich Mean Time - GMT => with this `if` it prints GMT+00:00 - Greenwhich Mean Time
             */
            if (!GMT_TRANSLATIONS.includes(locationName) && !formattedTimeZone.includes(locationName)) {
                formattedTimeZone = formattedTimeZone ? `${formattedTimeZone} - ${locationName}` : locationName;
            }
        }
    } catch (excp) {
        logException(excp);
    }

    return formattedTimeZone;
};
